import { useEffect, useState, useRef } from 'react';
import { customerPortalAPI } from '../api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { 
  Upload, FileImage, FileVideo, File, Trash2, Eye, Plus, 
  CheckCircle, Clock, AlertCircle, Image, Video, X, Loader2
} from 'lucide-react';

const statusColors = {
  pending: 'bg-amber-100 text-amber-700',
  reviewed: 'bg-blue-100 text-blue-700',
  resolved: 'bg-emerald-100 text-emerald-700',
  archived: 'bg-slate-100 text-slate-700'
};

const categoryColors = {
  issue: 'bg-red-100 text-red-700',
  success: 'bg-emerald-100 text-emerald-700',
  maintenance: 'bg-blue-100 text-blue-700',
  general: 'bg-slate-100 text-slate-700'
};

const categoryDescriptions = {
  issue: 'Report a problem or malfunction',
  success: 'Share a positive outcome or achievement',
  maintenance: 'Document maintenance or service work',
  general: 'Other uploads'
};

/**
 * CustomerPortalFilesPage - File upload and management for customers
 */
const CustomerPortalFilesPage = () => {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [viewFile, setViewFile] = useState(null);
  const [viewLoading, setViewLoading] = useState(false);
  const fileInputRef = useRef(null);
  
  // Upload form state
  const [uploadForm, setUploadForm] = useState({
    title: '',
    description: '',
    category: 'general'
  });
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState({ done: 0, total: 0 });

  useEffect(() => {
    fetchFiles();
  }, []);

  const fetchFiles = async () => {
    try {
      setLoading(true);
      const res = await customerPortalAPI.getMyFiles();
      setFiles(res.data.files || []);
    } catch (error) {
      toast.error('Failed to fetch files');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const picked = Array.from(e.target.files || []);
    if (!picked.length) return;
    const valid = [];
    for (const f of picked) {
      if (f.size > 10 * 1024 * 1024) {
        toast.error(`${f.name} exceeds the 10MB limit and was skipped`);
        continue;
      }
      valid.push(f);
    }
    setSelectedFiles(prev => [...prev, ...valid]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeSelectedFile = (idx) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== idx));
  };

  const handleUpload = async () => {
    if (!selectedFiles.length || !uploadForm.title) {
      toast.error('Please provide a title and select at least one file');
      return;
    }

    try {
      setUploading(true);
      setUploadProgress({ done: 0, total: selectedFiles.length });
      let success = 0;
      for (let i = 0; i < selectedFiles.length; i++) {
        const f = selectedFiles[i];
        const formData = new FormData();
        formData.append('file', f);
        const title = selectedFiles.length > 1 ? `${uploadForm.title} (${i + 1})` : uploadForm.title;
        formData.append('title', title);
        formData.append('description', uploadForm.description || '');
        formData.append('category', uploadForm.category);
        try {
          await customerPortalAPI.uploadFile(formData);
          success++;
        } catch (err) {
          toast.error(`Failed to upload ${f.name}`);
        }
        setUploadProgress({ done: i + 1, total: selectedFiles.length });
      }

      if (success > 0) {
        toast.success(`${success} file${success > 1 ? 's' : ''} uploaded successfully!`);
        setShowUploadDialog(false);
        setUploadForm({ title: '', description: '', category: 'general' });
        setSelectedFiles([]);
        if (fileInputRef.current) fileInputRef.current.value = '';
        fetchFiles();
      }
    } finally {
      setUploading(false);
      setUploadProgress({ done: 0, total: 0 });
    }
  };

  const handleView = async (fileId) => {
    try {
      setViewLoading(true);
      setViewFile({ loading: true });
      const res = await customerPortalAPI.getFile(fileId);
      setViewFile(res.data);
    } catch (error) {
      toast.error('Failed to load file');
      setViewFile(null);
    } finally {
      setViewLoading(false);
    }
  };

  const handleDelete = async (fileId) => {
    if (!confirm('Are you sure you want to delete this file?')) return;
    
    try {
      await customerPortalAPI.deleteFile(fileId);
      toast.success('File deleted');
      fetchFiles();
    } catch (error) {
      toast.error('Failed to delete file');
    }
  };

  const getFileIcon = (fileType) => {
    switch (fileType) {
      case 'image': return <FileImage className="h-5 w-5 text-blue-600" />;
      case 'video': return <FileVideo className="h-5 w-5 text-purple-600" />;
      default: return <File className="h-5 w-5 text-slate-600" />;
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending': return <Clock className="h-4 w-4" />;
      case 'reviewed': return <Eye className="h-4 w-4" />;
      case 'resolved': return <CheckCircle className="h-4 w-4" />;
      default: return null;
    }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="customer-portal-files">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">My Uploads</h1>
          <p className="text-muted-foreground">Upload and manage your files</p>
        </div>
        <Button onClick={() => setShowUploadDialog(true)} data-testid="new-upload-btn">
          <Plus className="h-4 w-4 mr-2" />
          Upload New File
        </Button>
      </div>

      {/* Upload Tips */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="pt-6">
          <div className="flex gap-4">
            <div className="p-3 bg-blue-100 rounded-lg h-fit">
              <Upload className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <h3 className="font-semibold text-blue-900 mb-1">Upload Tips</h3>
              <ul className="text-sm text-blue-700 space-y-1">
                <li>• Supported formats: Images (JPG, PNG, WebP), Videos (MP4, MOV)</li>
                <li>• Maximum file size: 10MB</li>
                <li>• Add clear titles and descriptions for faster review</li>
                <li>• Select the appropriate category for your upload</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Files List */}
      <Card>
        <CardHeader>
          <CardTitle>Uploaded Files</CardTitle>
          <CardDescription>Track the status of your submissions</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : files.length > 0 ? (
            <div className="space-y-3">
              {files.map((file) => (
                <div 
                  key={file.id} 
                  className="flex items-center justify-between p-4 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors"
                  data-testid={`file-item-${file.id}`}
                >
                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-white rounded-lg shadow-sm">
                      {getFileIcon(file.file_type)}
                    </div>
                    <div>
                      <p className="font-medium">{file.title}</p>
                      <div className="flex items-center gap-3 text-sm text-muted-foreground">
                        <span>{file.file_name}</span>
                        <span>•</span>
                        <span>{format(new Date(file.created_at), 'MMM d, yyyy')}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Badge className={categoryColors[file.category]}>
                      {file.category}
                    </Badge>
                    <Badge className={`${statusColors[file.status]} flex items-center gap-1`}>
                      {getStatusIcon(file.status)}
                      {file.status}
                    </Badge>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleView(file.id)}
                      title="View file"
                      data-testid={`view-file-${file.id}`}
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                    {file.status === 'pending' && (
                      <Button 
                        variant="ghost" 
                        size="icon"
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        onClick={() => handleDelete(file.id)}
                        data-testid={`delete-file-${file.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <FileImage className="h-16 w-16 mx-auto mb-4 text-slate-300" />
              <h3 className="text-lg font-medium text-slate-700 mb-2">No files uploaded yet</h3>
              <p className="text-muted-foreground mb-4">
                Upload photos or videos of your equipment, issues, or successes
              </p>
              <Button onClick={() => setShowUploadDialog(true)}>
                <Upload className="h-4 w-4 mr-2" />
                Upload Your First File
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Upload Dialog */}
      <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Upload New File</DialogTitle>
            <DialogDescription>
              Share photos or videos with our team
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {/* File Drop Zone */}
            <div 
              className="border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-colors border-slate-300 hover:border-blue-400 hover:bg-blue-50"
              onClick={() => fileInputRef.current?.click()}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*,video/*"
                multiple
                onChange={handleFileSelect}
                className="hidden"
                data-testid="file-input"
              />
              <div className="flex justify-center gap-4 mb-3">
                <Image className="h-9 w-9 text-slate-400" />
                <Video className="h-9 w-9 text-slate-400" />
              </div>
              <p className="font-medium text-slate-700">Click to select photos or videos</p>
              <p className="text-sm text-muted-foreground">You can select multiple files</p>
            </div>

            {/* Selected files preview */}
            {selectedFiles.length > 0 && (
              <div className="space-y-2" data-testid="selected-files-list">
                <p className="text-sm font-medium text-slate-700">{selectedFiles.length} file(s) selected</p>
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                  {selectedFiles.map((f, idx) => {
                    const isImage = f.type.startsWith('image/');
                    const previewUrl = isImage ? URL.createObjectURL(f) : null;
                    return (
                      <div key={idx} className="relative group border rounded-lg overflow-hidden bg-slate-50">
                        {isImage ? (
                          <img src={previewUrl} alt={f.name} className="h-20 w-full object-cover" />
                        ) : (
                          <div className="h-20 w-full flex items-center justify-center">
                            <FileVideo className="h-8 w-8 text-purple-500" />
                          </div>
                        )}
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); removeSelectedFile(idx); }}
                          className="absolute top-1 right-1 bg-black/60 text-white rounded-full p-0.5 opacity-90 hover:bg-black/80"
                          data-testid={`remove-selected-${idx}`}
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                        <p className="text-[10px] truncate px-1 py-0.5 text-slate-600">{f.name}</p>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Title */}
            <div>
              <Label htmlFor="title">Title *</Label>
              <Input
                id="title"
                placeholder="e.g., Machine maintenance photo"
                value={uploadForm.title}
                onChange={(e) => setUploadForm(prev => ({ ...prev, title: e.target.value }))}
                data-testid="upload-title-input"
              />
            </div>

            {/* Category */}
            <div>
              <Label>Category</Label>
              <Select 
                value={uploadForm.category} 
                onValueChange={(value) => setUploadForm(prev => ({ ...prev, category: value }))}
              >
                <SelectTrigger data-testid="upload-category-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(categoryDescriptions).map(([key, desc]) => (
                    <SelectItem key={key} value={key}>
                      <div>
                        <span className="font-medium capitalize">{key}</span>
                        <span className="text-muted-foreground ml-2">- {desc}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Description */}
            <div>
              <Label htmlFor="description">Description (optional)</Label>
              <Textarea
                id="description"
                placeholder="Provide any additional details..."
                value={uploadForm.description}
                onChange={(e) => setUploadForm(prev => ({ ...prev, description: e.target.value }))}
                rows={3}
                data-testid="upload-description-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUploadDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleUpload} disabled={uploading || !selectedFiles.length || !uploadForm.title} data-testid="confirm-upload-btn">
              {uploading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Uploading {uploadProgress.done}/{uploadProgress.total}...
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4 mr-2" />
                  Upload {selectedFiles.length > 1 ? `${selectedFiles.length} Files` : 'File'}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View File Dialog */}
      <Dialog open={!!viewFile} onOpenChange={() => setViewFile(null)}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>{viewFile?.title || 'View File'}</DialogTitle>
            <DialogDescription>{viewFile?.file_name}</DialogDescription>
          </DialogHeader>
          <div className="flex items-center justify-center min-h-[200px] bg-slate-50 rounded-lg overflow-hidden">
            {viewLoading || viewFile?.loading ? (
              <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
            ) : viewFile?.file_type === 'image' ? (
              <img src={viewFile.file_url} alt={viewFile.title} className="max-h-[60vh] w-auto object-contain" data-testid="view-file-image" />
            ) : viewFile?.file_type === 'video' ? (
              <video src={viewFile.file_url} controls className="max-h-[60vh] w-full" data-testid="view-file-video" />
            ) : viewFile?.file_url ? (
              <a href={viewFile.file_url} download={viewFile.file_name} className="text-blue-600 underline p-8">
                Download {viewFile.file_name}
              </a>
            ) : (
              <p className="text-muted-foreground p-8">Preview not available</p>
            )}
          </div>
          {viewFile?.description && (
            <p className="text-sm text-muted-foreground">{viewFile.description}</p>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CustomerPortalFilesPage;
